/**
 * DemocratCharts — auth hook.
 *
 * Provides login(), logout(), and hydrate() helpers. Components use these
 * instead of touching the API or store directly.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";

export function useAuth() {
  const navigate = useNavigate();
  const { accessToken, user, loading, setAccessToken, setUser, clear, setLoading } =
    useAuthStore();

  const login = useCallback(
    async (email: string, password: string) => {
      const resp = await api.post("/auth/login", { email, password });
      const token = resp.data.access_token as string;
      setAccessToken(token);
      // Fetch user info right after login
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    },
    [setAccessToken, setUser]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore — clearing local state regardless */
    }
    clear();
    navigate("/login", { replace: true });
  }, [clear, navigate]);

  /** Called once on app mount — try refresh + /me. If both fail, user stays logged out. */
  const hydrate = useCallback(async () => {
    try {
      const refresh = await api.post("/auth/refresh");
      const token = refresh.data.access_token as string;
      setAccessToken(token);
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {
      clear();
    } finally {
      setLoading(false);
    }
  }, [setAccessToken, setUser, clear, setLoading]);

  return {
    accessToken,
    user,
    loading,
    isAuthenticated: !!accessToken && !!user,
    login,
    logout,
    hydrate,
  };
}
